import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";

import PageBanner from "../../components/PageBanner";
import {
  editFormPaperSx,
  editPrimaryColumnSx,
  editSideColumnSx,
  editSplitWrapperSx,
} from "../../components/editPageStyles";
import { AppConfirmDialog, AppToast } from "../../components/AppFeedback";
import DatagridNavLink from "../../components/DatagridNavLink";
import {
  deleteClientDocument,
  fetchClientDocuments,
  fetchClientProjectsSummary,
  getClientByCid,
  getClientDocumentDownloadUrl,
  updateClient,
  uploadClientDocument,
} from "../../services/clientService";
import { COUNTRIES } from "../../constants/countries";
import { useUserContext } from "../../context/UserContext";
import { formatDate } from "../../utils/dateFormat";

const statusOptions = ["pending", "active", "completed", "submitted"];
const doctypeOptions = [
  { value: "admin", label: "Admin" },
  { value: "billing", label: "Billing" },
  { value: "costing", label: "Costing" },
  { value: "approval", label: "Approval" },
];

function toTitleCase(value = "") {
  const v = String(value || "").trim();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

function formatBytes(bytes) {
  if (!bytes || Number(bytes) <= 0) return "0 B";
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

const ClientEdit = () => {
  const navigate = useNavigate();
  const { cid } = useParams();
  const { roles = [] } = useUserContext();
  const isAdmin = roles.some((role) => String(role).toUpperCase() === "ADMIN");
  const isReadOnly = !isAdmin;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const [cidValue, setCidValue] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    status: "pending",
    contact_first_name: "",
    contact_last_name: "",
    email: "",
    phone_local: "",
    address1: "",
    address2: "",
    country: "United States",
    city: "",
    state: "",
    zipcode: "",
  });
  const [initialFormData, setInitialFormData] = useState(null);

  const [projectRows, setProjectRows] = useState([]);
  const [documentRows, setDocumentRows] = useState([]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    doctype: "admin",
    document_name: "",
    file: null,
  });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const selectedCountry = useMemo(
    () => COUNTRIES.find((country) => country.name === formData.country),
    [formData.country]
  );

  const loadProjectsAndDocuments = async (clientCid) => {
    setProjectsLoading(true);
    setDocumentsLoading(true);

    try {
      const [projects, docs] = await Promise.all([
        fetchClientProjectsSummary(clientCid),
        fetchClientDocuments(clientCid),
      ]);

      setProjectRows(
        (projects || []).map((project) => ({
          id: project.id,
          project_name: project.name,
          start_date: project.start_date,
          end_date: project.end_date,
          status: project.status,
        }))
      );

      setDocumentRows(
        (docs || []).map((doc) => ({
          ...doc,
          id: doc.id,
        }))
      );
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to load project/documents",
        severity: "error",
      });
    } finally {
      setProjectsLoading(false);
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadClient = async () => {
      setLoading(true);
      try {
        const data = await getClientByCid(cid);
        if (cancelled) return;

        const countryName = data.country || "United States";
        const countryConfig = COUNTRIES.find((country) => country.name === countryName);
        let phoneLocal = data.phone || "";
        if (countryConfig?.dialCode && phoneLocal.startsWith(countryConfig.dialCode)) {
          phoneLocal = phoneLocal.slice(countryConfig.dialCode.length);
        }

        setFormData({
          name: data.name || "",
          status: data.status || "pending",
          contact_first_name: data.contact_first_name || "",
          contact_last_name: data.contact_last_name || "",
          email: data.email || "",
          phone_local: phoneLocal,
          address1: data.address1 || "",
          address2: data.address2 || "",
          country: countryName,
          city: data.city || "",
          state: data.state || "",
          zipcode: data.zipcode || "",
        });
        setInitialFormData({
          name: data.name || "",
          status: data.status || "pending",
          contact_first_name: data.contact_first_name || "",
          contact_last_name: data.contact_last_name || "",
          email: data.email || "",
          phone_local: phoneLocal,
          address1: data.address1 || "",
          address2: data.address2 || "",
          country: countryName,
          city: data.city || "",
          state: data.state || "",
          zipcode: data.zipcode || "",
        });
        setCidValue(data.cid || cid || "");

        await loadProjectsAndDocuments(data.cid || cid);
      } catch (error) {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: error.message || "Failed to load client",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (cid) {
      loadClient();
    }

    return () => {
      cancelled = true;
    };
  }, [cid]);

  const handleChange = (event) => {
    if (isReadOnly) return;
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updatePayload = useMemo(
    () => ({
      name: formData.name,
      status: formData.status,
      contact_first_name: formData.contact_first_name,
      contact_last_name: formData.contact_last_name,
      phone: `${selectedCountry?.dialCode || ""}${(formData.phone_local || "").trim()}`,
      address1: formData.address1,
      address2: formData.address2,
      country: formData.country,
      city: formData.city,
      state: formData.state,
      zipcode: formData.zipcode,
    }),
    [formData, selectedCountry]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!cid || isReadOnly) return;

    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: "Client name is required", severity: "warning" });
      return;
    }
    if (!formData.contact_first_name.trim() || !formData.contact_last_name.trim()) {
      setSnackbar({ open: true, message: "Contact first and last name are required", severity: "warning" });
      return;
    }
    if (!formData.email.trim()) {
      setSnackbar({ open: true, message: "Email is required", severity: "warning" });
      return;
    }
    if (!(formData.phone_local || "").trim()) {
      setSnackbar({ open: true, message: "Phone is required", severity: "warning" });
      return;
    }
    if (
      !formData.address1.trim() ||
      !formData.city.trim() ||
      !formData.state.trim() ||
      !formData.zipcode.trim() ||
      !formData.country
    ) {
      setSnackbar({
        open: true,
        message: "Address is required (line 1, city, state, zip, country)",
        severity: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      await updateClient(cid, updatePayload);
      setSnackbar({
        open: true,
        message: "Client updated successfully",
        severity: "success",
      });
      setTimeout(() => navigate("/client-management"), 500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to update client",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (isReadOnly || !initialFormData) return;
    setFormData(initialFormData);
  };

  const handleUploadChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const setUploadFile = (file) => {
    if (!file) return;
    setUploadForm((prev) => ({
      ...prev,
      file,
      document_name: prev.document_name || file.name,
    }));
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    setUploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!cidValue || !uploadForm.file || !uploadForm.document_name.trim()) {
      setSnackbar({
        open: true,
        message: "Document type, name and file are required",
        severity: "warning",
      });
      return;
    }

    setUploading(true);
    try {
      await uploadClientDocument(cidValue, {
        doctype: uploadForm.doctype,
        document_name: uploadForm.document_name.trim(),
        file: uploadForm.file,
      });
      setSnackbar({ open: true, message: "Document uploaded", severity: "success" });
      setUploadOpen(false);
      setUploadForm({ doctype: "admin", document_name: "", file: null });
      await loadProjectsAndDocuments(cidValue);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to upload document",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!cidValue || !deleteDocumentId) return;
    try {
      await deleteClientDocument(cidValue, deleteDocumentId);
      setDeleteDocumentId(null);
      setSnackbar({ open: true, message: "Document deleted", severity: "success" });
      await loadProjectsAndDocuments(cidValue);
    } catch (error) {
      setDeleteDocumentId(null);
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete document",
        severity: "error",
      });
    }
  };

  const handleDownloadDocument = async (row) => {
    if (!cidValue || !row?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(getClientDocumentDownloadUrl(cidValue, row.id), {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to download document");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = row.filename || row.document_name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to download document",
        severity: "error",
      });
    }
  };

  const projectColumns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 130,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/project-details/${params.row.id}`)}
          >
            Open
          </Button>
        </Box>
      ),
    },
    {
      field: "project_name",
      headerName: "Project Name",
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <DatagridNavLink to={params.row.id ? `/project-details/${params.row.id}` : ""}>
          {params.value}
        </DatagridNavLink>
      ),
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "end_date",
      headerName: "End Date",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => toTitleCase(params.value),
    },
  ];

  const documentColumns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 140,
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip title="Download">
            <IconButton size="small" color="primary" onClick={() => handleDownloadDocument(params.row)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isAdmin ? (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteDocumentId(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      field: "doctype",
      headerName: "Document Type",
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => toTitleCase(params.value),
    },
    { field: "document_name", headerName: "Document Name", minWidth: 190, flex: 1.2 },
    { field: "filename", headerName: "Filename", minWidth: 180, flex: 1 },
    {
      field: "byte_size",
      headerName: "Size",
      minWidth: 100,
      flex: 0.6,
      renderCell: (params) => formatBytes(params.value),
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <PageBanner title={`Client / ${formData.name?.trim() || cidValue || "Details"}`} />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ mt: 3, mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="text"
            onClick={() => navigate("/client-management")}
            sx={{ mb: 2 }}
          >
            Back to Client Management
          </Button>
          <Typography variant="h5" fontWeight="bold" mb={2}>
            {isReadOnly ? "Client Details" : "Edit Client"}
          </Typography>
        </Box>

        {loading ? (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          </Paper>
        ) : (
          <Box sx={editSplitWrapperSx}>
            <Paper elevation={3} sx={editPrimaryColumnSx}>
                <Box component="form" onSubmit={handleSubmit}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="cid"
                        label="CID"
                        value={cidValue}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="name"
                        label="Client Name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        name="status"
                        label="Status"
                        value={formData.status}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                    Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="contact_first_name"
                        label="Contact First Name"
                        value={formData.contact_first_name}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="contact_last_name"
                        label="Contact Last Name"
                        value={formData.contact_last_name}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        name="email"
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled
                        InputProps={{ readOnly: true }}
                        helperText="Email cannot be changed after the client is created."
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Country Code" value={selectedCountry?.dialCode || ""} disabled />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        required
                        name="phone_local"
                        label="Mobile Number"
                        value={formData.phone_local}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                    Address
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        name="address1"
                        label="Address Line 1"
                        value={formData.address1}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="address2"
                        label="Address Line 2"
                        value={formData.address2}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        select
                        name="country"
                        label="Country"
                        value={formData.country}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        {COUNTRIES.map((country) => (
                          <MenuItem key={country.code} value={country.name}>
                            {country.name} ({country.dialCode})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        name="city"
                        label="City"
                        value={formData.city}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        name="state"
                        label="State"
                        value={formData.state}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        required
                        name="zipcode"
                        label="Zip Code"
                        value={formData.zipcode}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    {!isReadOnly ? (
                      <>
                        <Button variant="outlined" onClick={handleReset}>
                          Reset
                        </Button>
                        <Button type="submit" variant="contained" disabled={saving}>
                          {saving ? "Saving..." : "Update Client"}
                        </Button>
                      </>
                    ) : null}
                  </Box>
                </Box>
              </Paper>

            <Box sx={editSideColumnSx}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper elevation={3} sx={editFormPaperSx}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Client Documents
                      </Typography>
                      {isAdmin ? (
                        <Button variant="contained" onClick={() => setUploadOpen(true)}>
                          Add Document
                        </Button>
                      ) : null}
                    </Box>
                    <DataGrid
                      rows={documentRows}
                      columns={documentColumns}
                      loading={documentsLoading}
                      autoHeight
                      disableRowSelectionOnClick
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
                      }
                      pageSizeOptions={[5, 10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
                      sx={{
                        "& .row-even": {
                          backgroundColor: "#ffffff",
                        },
                        "& .row-odd": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={3} sx={editFormPaperSx}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Associated Projects
                    </Typography>
                    <DataGrid
                      rows={projectRows}
                      columns={projectColumns}
                      loading={projectsLoading}
                      autoHeight
                      disableRowSelectionOnClick
                      getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
                      }
                      pageSizeOptions={[5, 10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
                      sx={{
                        "& .row-even": {
                          backgroundColor: "#ffffff",
                        },
                        "& .row-odd": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Container>

      <Dialog
        open={uploadOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setUploadOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Add Client Document
          <IconButton
            aria-label="close"
            onClick={() => setUploadOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
            <TextField
              select
              name="doctype"
              label="Document Type"
              value={uploadForm.doctype}
              onChange={handleUploadChange}
              fullWidth
            >
              {doctypeOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="document_name"
              label="Document Name"
              value={uploadForm.document_name}
              onChange={handleUploadChange}
              fullWidth
            />

            <Box
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "1px dashed #9aa4b2",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragActive ? "#eef4ff" : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Drag and drop file here, or click to select
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {uploadForm.file ? `Selected: ${uploadForm.file.name}` : "No file selected"}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleFileInput}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <AppConfirmDialog
        open={Boolean(deleteDocumentId)}
        title="Delete Document"
        content="Are you sure you want to delete this document?"
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteDocumentId(null)}
        onConfirm={handleDeleteDocument}
      />

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ClientEdit;
